import { useState, useCallback } from 'react';
import { buildTaskTree, flattenTaskTree } from '../../../features/tasks/utils/taskUtils';
import { isPopupOpen } from '../../../shared/utils/popupUtils';
import { getPresetDisplayName } from '../../../shared/utils/presets';

/**
 * Hook for handling file name editing
 * @param {Object} params - Configuration object
 * @param {Array} params.files - All files
 * @param {string} params.currentFileId - Current file ID
 * @param {Function} params.updateFileName - Function to update file name
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.expandedTasks - Set of expanded task IDs
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Object} params.editingElementRef - Ref to the editing element
 * @param {Object} params.pendingFocusRef - Ref for pending focus (focus first task after blur)
 */
export function useFileNameEditing({
  files,
  currentFileId,
  updateFileName,
  tasks,
  expandedTasks,
  setEditingTaskId,
  editingElementRef,
  pendingFocusRef
}) {
  const [editingFileName, setEditingFileName] = useState(false);

  // Get current file name (include preset display name when logged out and viewing example list)
  const currentFile = files.find(f => f.id === currentFileId);
  const currentFileName = currentFile
    ? currentFile.name
    : (currentFileId && currentFileId.startsWith('preset:')
      ? getPresetDisplayName(currentFileId.replace(/^preset:/, ''))
      : '');

  const handleFileNameClick = useCallback(() => {
    setEditingFileName(true);
  }, []);

  const handleFileNameBlur = useCallback(async (e) => {
    setEditingFileName(false);
    const newName = e.target.textContent || '';
    const trimmedName = newName.trim();
    
    if (trimmedName && currentFileId) {
      try {
        await updateFileName(currentFileId, trimmedName);
      } catch (error) {
        console.error('Error updating file name:', error);
        // Restore original name on error
        e.target.textContent = currentFile ? currentFile.name : '';
      }
    } else if (currentFile) {
      // Restore original name if empty
      e.target.textContent = currentFile.name;
    }
  }, [currentFileId, currentFile, updateFileName]);

  const commitAndFocusFirstTask = useCallback(async (e) => {
    const taskTree = buildTaskTree(tasks);
    const flattenedTasks = flattenTaskTree(taskTree, expandedTasks);
    const firstTask = flattenedTasks[0];
    const targetReactKey = firstTask ? firstTask._reactKey : 'placeholder_client_key';
    const newName = e.target.textContent || '';
    const trimmedName = newName.trim();
    if (trimmedName && currentFileId && trimmedName !== (currentFile ? currentFile.name : '')) {
      try {
        await updateFileName(currentFileId, trimmedName);
      } catch (error) {
        console.error('Error updating file name:', error);
      }
    }
    setEditingFileName(false);
    e.target.blur();
    if (pendingFocusRef) {
      pendingFocusRef.current = { reactKey: targetReactKey };
    }
    setEditingTaskId(targetReactKey);
  }, [tasks, expandedTasks, currentFileId, currentFile, updateFileName, setEditingTaskId, pendingFocusRef]);

  const handleFileNameKeyDown = useCallback(async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isPopupOpen()) {
        await commitAndFocusFirstTask(e);
      } else {
        e.target.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (currentFile) {
        e.target.textContent = currentFile.name;
      }
      setEditingFileName(false);
      e.target.blur();
    } else if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      if (!isPopupOpen()) {
        await commitAndFocusFirstTask(e);
      }
    }
  }, [currentFile, commitAndFocusFirstTask]);

  const focusFileName = useCallback(() => {
    setEditingFileName(true);
  }, []);

  return {
    editingFileName,
    currentFileName,
    handleFileNameClick,
    handleFileNameBlur,
    handleFileNameKeyDown,
    focusFileName
  };
}
